import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Save, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLE_TONE = {
  admin: "bg-slate-900 text-white border-slate-900",
  manager: "bg-blue-50 text-blue-700 border-blue-200",
  finance: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sales: "bg-amber-50 text-amber-700 border-amber-200",
};

const PermGrid = ({ sections, actions, value, onChange, testidPrefix, disabled }) => (
  <div className="overflow-x-auto border border-slate-200">
    <table className="w-full min-w-[520px] text-sm">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
          <th className="px-3 py-2">Panel</th>
          {actions.map((a) => (
            <th key={a} className="px-3 py-2 text-center">
              {a}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {sections.map((s) => (
          <tr key={s.key}>
            <td className="px-3 py-2 font-medium">{s.label}</td>
            {actions.map((a) => (
              <td key={a} className="px-3 py-2 text-center">
                <Checkbox
                  disabled={disabled}
                  data-testid={`${testidPrefix}-${s.key}-${a}`}
                  checked={!!value?.[s.key]?.[a]}
                  onCheckedChange={(c) =>
                    onChange({
                      ...value,
                      [s.key]: { ...(value?.[s.key] || {}), [a]: !!c, ...(a === "view" && !c ? { edit: false, delete: false } : {}) },
                    })
                  }
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const emptyPerms = (sections) =>
  Object.fromEntries(sections.map((s) => [s.key, { view: false, edit: false, delete: false }]));

export default function Team() {
  const { user, can } = useAuth();
  const canEdit = can("team", "edit");
  const [meta, setMeta] = useState({ sections: [], actions: [], roles: [] });
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(null);
  const [roleDraft, setRoleDraft] = useState({});

  const load = async () => {
    const [m, t, r] = await Promise.all([
      api.get("/team/sections"),
      api.get("/team"),
      api.get("/roles"),
    ]);
    setMeta(m.data);
    setMembers(t.data);
    setRoles(r.data);
    setRoleDraft(Object.fromEntries(r.data.map((x) => [x.role, x.permissions])));
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm({
      email: "",
      name: "",
      role: "manager",
      password: "",
      active: true,
      custom: false,
      permissions: roleDraft.manager || emptyPerms(meta.sections),
    });
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      email: m.email,
      name: m.name,
      role: m.role,
      password: "",
      active: m.active,
      custom: !!m.permissions,
      permissions: m.effective_permissions,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        role: form.role,
        active: form.active,
        permissions: form.custom ? form.permissions : null,
      };
      if (form.password) payload.password = form.password;
      if (editId) {
        await api.put(`/team/${editId}`, payload);
      } else {
        await api.post("/team", { ...payload, email: form.email, password: form.password });
      }
      toast.success(editId ? "Member updated" : "Member added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/team/${id}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveRole = async (role) => {
    try {
      await api.put(`/roles/${role}`, { permissions: roleDraft[role] });
      toast.success(`${role} rights updated`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="team-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Access Control
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">
            Team &amp; Access
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Add your team as Manager, Finance or Sales — then decide exactly which panels each person
            sees and what they can edit or delete.
          </p>
        </div>
        {canEdit && (
          <Button
            data-testid="add-member-button"
            className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
            onClick={openNew}
          >
            <UserPlus className="mr-2 h-4 w-4" /> Add member
          </Button>
        )}
      </div>

      <Tabs defaultValue="members">
        <TabsList className="mb-6 flex w-full max-w-full overflow-x-auto rounded-md bg-slate-100">
          <TabsTrigger value="members" data-testid="tab-members" className="rounded-md">
            Members
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles" className="rounded-md">
            <ShieldCheck className="mr-2 h-4 w-4" /> Role Defaults
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <div className="overflow-x-auto border border-slate-200 bg-white">
            <table className="w-full min-w-[900px] text-sm" data-testid="team-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Email</th>
                  <th className="px-3 py-3">Role</th>
                  <th className="px-3 py-3">Panels they can see</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((m) => (
                  <tr key={m.id} className="transition-colors hover:bg-slate-50" data-testid={`team-row-${m.id}`}>
                    <td className="px-3 py-2 font-semibold">
                      {m.name}
                      {m.email === user?.email && (
                        <span className="ml-2 text-[10px] font-bold uppercase text-slate-400">you</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{m.email}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`rounded-md border ${ROLE_TONE[m.role]}`}>
                        {m.role}
                      </Badge>
                      {m.permissions && (
                        <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">custom</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {Object.entries(m.effective_permissions || {})
                        .filter(([, v]) => v.view)
                        .map(([k]) => k)
                        .join(", ") || "none"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        variant="outline"
                        className={`rounded-md border ${
                          m.active
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-red-200 bg-red-50 text-red-700"
                        }`}
                      >
                        {m.active ? "active" : "disabled"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`edit-member-${m.id}`}
                            className="rounded-md border-slate-300"
                            onClick={() => openEdit(m)}
                          >
                            Rights
                          </Button>
                        )}
                        {can("team", "delete") && m.email !== user?.email && (
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`delete-member-${m.id}`}
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            onClick={() => remove(m.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="roles">
          <div className="space-y-6" data-testid="roles-panel">
            {roles
              .filter((r) => r.role !== "admin")
              .map((r) => (
                <div key={r.role} className="border border-slate-200 bg-white">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
                    <div>
                      <h3 className="font-display text-base font-bold capitalize tracking-tight">
                        {r.role}
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        Default rights for everyone with this role (unless you give them custom rights).
                      </p>
                    </div>
                    {canEdit && (
                      <Button
                        size="sm"
                        data-testid={`save-role-${r.role}`}
                        className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
                        onClick={() => saveRole(r.role)}
                      >
                        <Save className="mr-2 h-4 w-4" /> Save
                      </Button>
                    )}
                  </div>
                  <div className="p-5">
                    <PermGrid
                      sections={meta.sections}
                      actions={meta.actions}
                      value={roleDraft[r.role]}
                      disabled={!canEdit}
                      testidPrefix={`role-${r.role}`}
                      onChange={(v) => setRoleDraft({ ...roleDraft, [r.role]: v })}
                    />
                  </div>
                </div>
              ))}
            <div className="border border-slate-200 bg-slate-50 px-5 py-4 text-xs text-slate-500">
              Admin always keeps full access to every panel and cannot be restricted.
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              {editId ? "Member rights" : "Add a team member"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Pick a role for the defaults, then switch on custom rights to fine-tune what this person
              sees and can change.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-bold uppercase">Name</Label>
                  <Input
                    data-testid="member-name-input"
                    className="mt-1 h-10 border-slate-300"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Email</Label>
                  <Input
                    data-testid="member-email-input"
                    type="email"
                    disabled={!!editId}
                    className="mt-1 h-10 border-slate-300"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Role</Label>
                  <Select
                    value={form.role}
                    onValueChange={(v) =>
                      setForm({ ...form, role: v, permissions: roleDraft[v] || form.permissions })
                    }
                  >
                    <SelectTrigger data-testid="member-role-select" className="mt-1 h-10 border-slate-300 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border border-slate-200 bg-white">
                      {(meta.roles || []).map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">
                    {editId ? "New password (optional)" : "Password"}
                  </Label>
                  <Input
                    data-testid="member-password-input"
                    type="password"
                    className="mt-1 h-10 border-slate-300"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <Switch
                    data-testid="member-active-switch"
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                  />
                  Account active
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <Switch
                    data-testid="member-custom-switch"
                    checked={form.custom}
                    onCheckedChange={(v) =>
                      setForm({ ...form, custom: v, permissions: form.permissions || emptyPerms(meta.sections) })
                    }
                  />
                  Custom rights for this person
                </label>
              </div>

              {form.role !== "admin" && (
                <PermGrid
                  sections={meta.sections}
                  actions={meta.actions}
                  value={form.permissions}
                  disabled={!form.custom}
                  testidPrefix="member-perm"
                  onChange={(v) => setForm({ ...form, permissions: v })}
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="save-member-button"
              className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
              onClick={save}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
